import { Building2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useAccountBusinessUnits } from '@/hooks/use-business-units';

/** The sentinel for "no brand filter". Radix Select rejects an empty string value. */
const ACCOUNT_WIDE = 'all';

interface BrandScopePickerProps {
  /** The account being inspected — NOT the one the Control Panel is administering. */
  accountId: number;
  /** Selected brand id, or null for account-wide. */
  value: string | null;
  onChange: (buId: string | null) => void;
}

/**
 * Brand scope for one Internal Analytics panel.
 *
 * The product frontend follows the operator's own brand switcher. There is no equivalent
 * here: the Control Panel inspects other people's accounts, so the brand has to come from
 * the account on screen and is chosen per panel.
 *
 * Styled as the same bordered pill the date range picker uses, and placed beside it: both
 * narrow the numbers below, and a control that scopes the whole page should not read as a
 * third thing floating between the tabs and the date.
 *
 * Renders NOTHING for an account with fewer than two brands. Every account has a default
 * brand created with it, so a picker offering one option plus "All brands" would be two
 * ways of asking for the same rows on the great majority of accounts.
 */
export function BrandScopePicker({ accountId, value, onChange }: BrandScopePickerProps) {
  const units = useAccountBusinessUnits(accountId);

  // Inactive units are still selectable: they keep their history, and a retired brand's
  // past numbers are exactly what someone opens this page to look at.
  if (!units || units.length < 2) return null;

  return (
    <Select
      value={value ?? ACCOUNT_WIDE}
      onValueChange={(next) => onChange(next === ACCOUNT_WIDE ? null : next)}
    >
      <div className='flex cursor-pointer items-center gap-1 rounded-full border-[1.5px] border-gray-300 py-1 pl-3 pr-1'>
        <Building2 className='h-4 w-4 shrink-0 text-black-100' />
        {/* Same chip the date picker's Input uses, class for class. `data-[size=default]:h-6`
            as well as `h-6`: SelectTrigger ships `data-[size=default]:h-9`, and a plain
            height does not override a data-attribute variant — the chip rendered a
            different height from the date field beside it. */}
        <SelectTrigger
          aria-label='Brand'
          className='h-6 min-w-[175px] gap-2 rounded-full border-none bg-gray-100 px-2 text-xs shadow-none hover:bg-gray-200 focus-visible:ring-0 data-[size=default]:h-6'
        >
          <SelectValue placeholder='All brands' />
        </SelectTrigger>
      </div>
      <SelectContent align='end'>
        <SelectItem value={ACCOUNT_WIDE}>All brands</SelectItem>
        {units.map((bu) => (
          <SelectItem key={bu.id} value={String(bu.id)}>
            {bu.name}
            {bu.status === 'inactive' ? ' (inactive)' : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
